'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { getUserSpaces, switchUserSpace } from '@/app/apis';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { BaseLineSwap } from '@/app/components/icons/BaseLineSwap';
import { useCredits, useFetch, useSubscription } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { TeamSpaceInfo } from '@/app/types';
import {
  buildTeamSpaces,
  cn,
  findTeamSpaceById,
  isPersonalSpaceId,
  PERSONAL_TEAM_SPACE,
  PERSONAL_TEAM_SPACE_ID,
} from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { getSpaceAvatarColor, getSpaceInitials } from '../utils';

const TeamAvatar = ({ spaceId, teamName, className }: { spaceId: string; teamName?: string; className?: string }) => {
  if (spaceId === PERSONAL_TEAM_SPACE_ID) {
    return <BaseLineSwap className={cn('size-4', className)} />;
  }

  return (
    <div
      className={cn('flex items-center justify-center', className)}
      style={{ backgroundColor: getSpaceAvatarColor(spaceId) }}
    >
      {getSpaceInitials(teamName)}
    </div>
  );
};

export const TeamSpaceSwitcher = () => {
  const t = useTranslations('teamProfile');
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const { updateCredits } = useCredits();
  const { updateSubscription } = useSubscription();

  const {
    teamSpaceInfo,
    teamSpaces,
    userInfo,
    isUserInfoInitialized,
    updateUserInfo,
    updateTeamInfo,
    updateTeamSpaces,
    resetTaskFormStates,
    resetToolFormStates,
  } = useStore((state) => ({
    teamSpaceInfo: state.teamSpaceInfo,
    teamSpaces: state.teamSpaces,
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    updateUserInfo: state.updateUserInfo,
    updateTeamInfo: state.updateTeamInfo,
    updateTeamSpaces: state.updateTeamSpaces,
    resetTaskFormStates: state.resetTaskFormStates,
    resetToolFormStates: state.resetToolFormStates,
  }));

  const { run: fetchUserSpaces } = useFetch(getUserSpaces, {
    manual: true,
    onSuccess: ({ data }) => {
      updateTeamSpaces(buildTeamSpaces(data?.spaces));
    },
  });

  const { run: switchSpace } = useFetch(switchUserSpace, {
    manual: true,
    onSuccess: ({ data }) => {
      if (!data?.user) {
        return;
      }

      updateUserInfo(data.user);

      if (isPersonalSpaceId(data.user.internal?.space_id)) {
        updateTeamInfo(null);
      } else {
        updateTeamInfo(findTeamSpaceById(teamSpaces, data.user.internal?.space_id));
      }

      resetTaskFormStates();
      resetToolFormStates();
      updateCredits();
      updateSubscription();
      router.push('/home/recommend');
    },
  });

  useEffect(() => {
    if (!isUserInfoInitialized || !userInfo || teamSpaces.length > 0) {
      return;
    }
    fetchUserSpaces();
  }, [fetchUserSpaces, isUserInfoInitialized, teamSpaces.length, userInfo]);

  useEffect(() => {
    const currentTeamSpaceId = teamSpaceInfo?.space_id;
    if (isPersonalSpaceId(currentTeamSpaceId) || teamSpaces.length === 0) {
      return;
    }

    if (!teamSpaces.some((space) => space.space_id === currentTeamSpaceId)) {
      fetchUserSpaces();
    }
  }, [fetchUserSpaces, teamSpaceInfo, teamSpaces]);

  if (teamSpaces.length === 0) {
    return null;
  }

  const currentSpaceId = teamSpaceInfo?.space_id ?? PERSONAL_TEAM_SPACE_ID;
  const currentSpace = findTeamSpaceById(teamSpaces, currentSpaceId) ?? teamSpaceInfo ?? PERSONAL_TEAM_SPACE;

  const handleSpaceSwitch = (space: TeamSpaceInfo) => {
    if (space.space_id === currentSpaceId) {
      setOpen(false);
      return;
    }

    switchSpace(space.space_id ?? PERSONAL_TEAM_SPACE_ID);
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-6 cursor-pointer items-center gap-1 rounded-8 border border-system-white08 bg-system-bg02 py-1 pr-1 pl-2 transition-colors hover:border-system-white12 hover:bg-system-bg03"
          >
            <span className="max-w-[143px] truncate font-medium text-white text-xs leading-6">
              {currentSpace.space_id === PERSONAL_TEAM_SPACE_ID
                ? t('personalWorkspace')
                : t('workspaceWithName', { name: currentSpace.team_name || '' })}
            </span>
            <BaseLineRight className="size-4 shrink-0 rotate-90 text-white transition-transform" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          sideOffset={4}
          align="center"
          className="w-[328px] select-none rounded-12 border border-system-white12 bg-[#2C2E33] p-2 shadow-[0px_8px_24px_0px_rgba(0,0,0,0.32)]"
        >
          <div className="space-y-1">
            {teamSpaces.map((space) => {
              if (!space.space_id) {
                return null;
              }

              const isActive = space.space_id === currentSpaceId;
              const isPersonal = space.space_id === PERSONAL_TEAM_SPACE_ID;

              return (
                <button
                  key={space.space_id}
                  type="button"
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-8 px-2 py-3 text-left transition-colors',
                    isActive ? 'bg-system-white08' : 'hover:bg-system-white04',
                  )}
                  onClick={() => handleSpaceSwitch(space)}
                >
                  <div className="flex size-6 shrink-0 items-center justify-center text-white">
                    <TeamAvatar
                      spaceId={space.space_id}
                      teamName={space.team_name}
                      className="size-5 overflow-hidden rounded-6 font-medium text-sm"
                    />
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col truncate font-normal text-sm text-white leading-6">
                    {isPersonal
                      ? t('switchToPersonalWorkspace')
                      : t('switchToWorkspace', { name: space.team_name || '' })}
                  </div>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <div className="h-4 w-[1px] bg-system-white24" />
    </>
  );
};
